import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Box, IconButton, Typography, Button, CircularProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PageContainer from "../../common_components/PageContainer";
import ClientGroupHeader from "../../common_components/ClientGroupHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CommonTabs from "../../common_components/CommonTabs";
import CommonTable from "../../common_components/CommonTable";
import Pagination from "../../common_components/Pagination";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import { SearchInput } from "../../common_components/FormInputs";
import CommonDrawerLayout from "../../common_components/CommonDrawerLayout";
import AppButton from "../../common_components/AppButton";
import TakeActionContent from "../../common_components/TakeActionContent";
import CreateAdRequestDrawer from "./components/CreateAdRequestDrawer";
import AdPreview from "./components/AdPreview";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { FloatingSaveBar } from "../../common_components/FloatingSaveBar";
import { toast } from "../../common_components/Toast/ToastUtils";
import { sendEmailNotification } from "../../redux/emailSlice";
import { selectEntityType, selectUser } from "../../redux/authSlice";
import { triggerRefresh } from "../../redux/refreshSlice";
import { createApprovalActivity } from "../../redux/marketingCalendarSlice";
import { fetchClientAdApprovals, fetchAdPreview, deleteManualAdRequest, reorderClientAdApprovals, selectClientAdApprovalRows, selectClientAdApprovalRowsLoading, selectClientAdApprovalPagination, selectClientAdApprovalPreviewOptions, selectClientAdApprovalLastSyncedAt, selectTakeActionCloseNonce, clearList, } from "../../redux/clientAdApprovalSlice";
// ✅ Import Custom Hooks
import { useClientSelection, useAccountSelection, useDateRange } from '../../hooks';
import CustomDatePicker from "../../common_components/daterangepicker/CustomDatePicker";
// ============ CONSTANTS ============
const PAGE_SIZE = 20;
const ALL_TABS = [
    { id: "ready_for_approval", label: "Ready for Approval" },
    { id: "sent_for_client_approval", label: "Sent for Client Approval" },
    { id: "ad_approval_status", label: "Ad Approval Status" },
    { id: "social_ad_request_queue", label: "Social Ad Request Queue" },
];
const CHIP_STYLES = {
    pending: { bg: "#FEFCE8", color: "#936316" },
    approved: { bg: "#ECFDF3", color: "#027A48", label: "Approved" },
    rejected: { bg: "#FEF2F2", color: "#B42318", label: "Not Approved" },
};
// Caps the Ad Name column so long ad names don't force the table to overflow
// and push "Sent Status" off-screen — text truncates with an ellipsis instead.
const AD_NAME_CELL_SX = {
    maxWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};
const CHIP_BASE_SX = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    p: "4px 12px",
    borderRadius: "999px",
    fontSize: 14,
    fontWeight: 500,
    minWidth: 150,
    textAlign: "center",
};
// ============ HELPER FUNCTIONS ============
const getChipConfig = (status, tab, sentCount) => {
    const s = status?.toLowerCase?.() || "";
    if (tab === "ad_approval_status")
        return s === "approved" ? CHIP_STYLES.approved : CHIP_STYLES.rejected;
    if (tab === "social_ad_request_queue") {
        if (s === "approved" || s === "rejected" || s === "not_approved") {
            return { ...CHIP_STYLES.approved, label: "Action Taken", bg: "#ECFDF3", color: "#027A48" };
        }
        if (s === "pending" && sentCount > 0) {
            return { ...CHIP_STYLES.pending, label: `Waiting for Approval (${sentCount})` };
        }
    }
    if (tab === "sent_for_client_approval") {
        const label = sentCount > 0 ? `Waiting for Approval (${sentCount})` : "Waiting for Approval";
        return { ...CHIP_STYLES.pending, label };
    }
    return { ...CHIP_STYLES.pending, label: "Send for Approval" };
};
// ============ PAGE-SPECIFIC HOOKS ============
// ✅ Drag & Drop Hook (Page Specific)
const useDragDrop = (rows, setRows, onReorder) => {
    const draggingRef = useRef(null);
    const dragOverRef = useRef(null);
    const onStart = useCallback((id) => {
        draggingRef.current = id;
        dragOverRef.current = null;
    }, []);
    const onOver = useCallback((id, e) => {
        if (draggingRef.current && draggingRef.current !== id) {
            e.preventDefault();
            dragOverRef.current = id;
        }
    }, []);
    const onEnd = useCallback(() => {
        draggingRef.current = null;
        dragOverRef.current = null;
    }, []);
    const onDrop = useCallback((targetId) => {
        const draggingId = draggingRef.current;
        if (!draggingId || draggingId === targetId) {
            draggingRef.current = null;
            dragOverRef.current = null;
            return;
        }
        const fromIndex = rows.findIndex(r => r._id === draggingId);
        const toIndex = rows.findIndex(r => r._id === targetId);
        if (fromIndex === -1 || toIndex === -1) {
            draggingRef.current = null;
            dragOverRef.current = null;
            return;
        }
        const updatedRows = [...rows];
        const [movedItem] = updatedRows.splice(fromIndex, 1);
        updatedRows.splice(toIndex, 0, movedItem);
        setRows(updatedRows);
        if (onReorder) {
            onReorder(updatedRows.map(r => r._id));
        }
        draggingRef.current = null;
        dragOverRef.current = null;
    }, [rows, setRows, onReorder]);
    return { onStart, onOver, onDrop, onEnd, draggingRef, dragOverRef };
};
// ✅ Drawers Hook (Page Specific)
const useDrawers = () => {
    const [state, setState] = useState({
        preview: {
            open: false,
            row: null,
            format: "",
            html: null,
            url: null,
            error: null,
            loading: false
        },
        create: { open: false, editing: null },
        action: { open: false, row: null },
    });
    const open = useCallback((key, data = {}) => setState(p => ({ ...p, [key]: { ...p[key], open: true, ...data } })), []);
    const close = useCallback((key, reset = {}) => setState(p => ({ ...p, [key]: { ...p[key], open: false, ...reset } })), []);
    const update = useCallback((key, data) => setState(p => ({ ...p, [key]: { ...p[key], ...data } })), []);
    return { state, open, close, update };
};
// ============ MAIN COMPONENT ============
const ClientAdApprovalPage = () => {
    const dispatch = useDispatch();
    const entityType = useSelector(selectEntityType);
    const user = useSelector(selectUser);
    const permissions = useSelector((state) => state.auth.permissions);
    // ✅ Custom Hooks - Client Selection
    const { selectedId, selectedClientId, selectedGroupAccountId, clientOptions, selectedClient, // ✅ Added this
    groupAccountOptions, currentSelectedGroupAccountLabel, handleClientChange, handleGroupChange, utilityLoading, } = useClientSelection("ad_approvals");
    // ✅ Custom Hooks - Account Selection
    const { connectionId, setConnectionId, accountOptions, accountLoading, currentAccountLabel, showAccountField } = useAccountSelection({ selectedId });
    // ✅ Date Range Hook (MTD through Today without comparison)
    const { dateRange, handleDateChange } = useDateRange({ defaultComparison: false, defaultEndToToday: true });
    // ✅ Page-Specific Hooks
    const drawers = useDrawers();
    // ✅ Redux Selectors
    const rows = useSelector(selectClientAdApprovalRows);
    const rowsLoading = useSelector(selectClientAdApprovalRowsLoading);
    const lastSyncedAt = useSelector(selectClientAdApprovalLastSyncedAt);
    const previewOptions = useSelector(selectClientAdApprovalPreviewOptions);
    const pagination = useSelector(selectClientAdApprovalPagination);
    const closeNonce = useSelector(selectTakeActionCloseNonce);
    const safePreviewOptions = Array.isArray(previewOptions) ? previewOptions : [];
    // ✅ Permissions
    const { access = true, actions = [] } = permissions?.ad_approvals || {};
    const canManage = actions.some((a) => ["client approval & comments", "manage ad requests"].includes(a.toLowerCase().trim()));
    const canManageAdRequests = actions.some((a) => a.toLowerCase().trim() === "manage ad requests");
    const isClient = entityType === "client";
    const canInteract = canManage || isClient;
    // ✅ Tab Config based on entity type
    const TAB_CONFIG = useMemo(() => {
        if (entityType === "client") {
            return ALL_TABS.filter(tab => tab.id !== "ready_for_approval").map(tab => {
                if (tab.id === "sent_for_client_approval") {
                    return { ...tab, label: "Waiting for Approval" };
                }
                return tab;
            });
        }
        return ALL_TABS;
    }, [entityType]);
    // ✅ Local State
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") || "ready_for_approval";
    const [activeTab, setActiveTab] = useState(initialTab);
    const [displayRows, setDisplayRows] = useState(rows || []);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({
        columnId: "order",
        direction: "asc"
    });
    const [selected, setSelected] = useState(new Set());
    const [syncing, setSyncing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [bulkSending, setBulkSending] = useState(false);
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        id: null
    });
    const [sidebarOffset, setSidebarOffset] = useState(363);
    const isQueue = activeTab === "social_ad_request_queue";
    const isLoading = utilityLoading || accountLoading;
    // ✅ Sidebar offset tracking
    useEffect(() => {
        const container = document.querySelector('.content-container');
        const checkOffset = () => {
            if (container) {
                const ml = window.getComputedStyle(container).marginLeft;
                setSidebarOffset(parseFloat(ml) || 0);
            }
        };
        checkOffset();
        let observer = null;
        if (container) {
            observer = new MutationObserver(checkOffset);
            observer.observe(container, { attributes: true, attributeFilter: ['style', 'class'] });
        }
        window.addEventListener('resize', checkOffset);
        return () => {
            window.removeEventListener('resize', checkOffset);
            observer?.disconnect();
        };
    }, []);
    // ✅ Handle entity type tab restriction
    useEffect(() => {
        if (entityType === "client" && activeTab === "ready_for_approval") {
            setActiveTab("sent_for_client_approval");
        }
    }, [entityType, activeTab]);
    // ✅ Sync displayRows with rows
    useEffect(() => {
        setDisplayRows(rows || []);
    }, [rows]);
    // ✅ Reorder Handler
    const handleReorder = useCallback(async (ids) => {
        if (!connectionId || !ids.length)
            return;
        setSort({ columnId: "order", direction: "asc" });
        try {
            await dispatch(reorderClientAdApprovals({
                connectionId,
                tab: activeTab,
                orderedIds: ids,
            })).unwrap();
            toast.success("Reordered successfully");
        }
        catch (e) {
            toast.error(e?.message || "Reorder failed");
        }
    }, [connectionId, activeTab, dispatch]);
    // ✅ Drag & Drop
    const drag = useDragDrop(displayRows, setDisplayRows, handleReorder);
    const promiseRef = useRef(null);
    // ✅ Fetch Data
    const fetchData = useCallback(async () => {
        if (promiseRef.current) {
            promiseRef.current.abort();
            promiseRef.current = null;
        }
        if (!connectionId) {
            dispatch(clearList());
            return;
        }
        const params = new URLSearchParams({
            connectionId,
            tab: activeTab,
            page: String(page),
            limit: String(PAGE_SIZE),
            clientId: selectedId,
            parentId: selectedClientId,
            childId: selectedGroupAccountId || '',
        });
        if (activeTab === 'ad_approval_status' && dateRange?.start && dateRange?.end) {
            params.append("startDate", dateRange.start.toString());
            params.append("endDate", dateRange.end.toString());
        }
        if (search.trim())
            params.append("searchText", search.trim());
        if (sort) {
            params.append("sortBy", sort.columnId);
            params.append("sortOrder", sort.direction);
        }
        try {
            const promise = dispatch(fetchClientAdApprovals({ params }));
            promiseRef.current = promise;
            await promise.unwrap();
        }
        catch (e) {
            if (e?.name !== 'AbortError' && e?.message !== 'Aborted') {
                toast.error(e?.message || "Error fetching data");
            }
        }
    }, [dispatch, connectionId, activeTab, page, search, sort, selectedGroupAccountId, selectedId, selectedClientId, dateRange]);
    // ✅ Reset on filter change
    useEffect(() => {
        setPage(1);
        setSelected(new Set());
    }, [connectionId, activeTab, search, dateRange]);
    // ✅ Fetch data effect
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    // ✅ Close action drawer on nonce change
    useEffect(() => {
        if (drawers.state.action.open) {
            drawers.close("action", { row: null });
        }
    }, [closeNonce]);
    // ✅ Sync Handler
    const handleSync = useCallback(async () => {
        if (!selectedClientId)
            return;
        try {
            setSyncing(true);
            const resultAction = await dispatch(triggerRefresh({
                clientId: selectedClientId,
                connectionId: connectionId || undefined
            }));
            if (triggerRefresh.fulfilled.match(resultAction)) {
                await fetchData();
                toast.success("Data synced successfully");
            }
            else {
                toast.error(resultAction.payload || "Sync failed");
            }
        }
        catch (e) {
            toast.error(e?.message || "Sync error");
        }
        finally {
            setSyncing(false);
        }
    }, [dispatch, selectedClientId, connectionId, fetchData]);
    // ✅ Fetch Preview
    const fetchPreview = useCallback(async (row, format) => {
        if (!row?.adId)
            return toast.error("Preview unavailable. This ad does not have a valid Ad ID.");
        const acc = accountOptions.find(o => o.value === connectionId);
        const accId = acc?.accountId || acc?.value;
        if (!accId)
            return toast.error("Unable to preview. No Meta account is linked to this connection.");
        drawers.update("preview", { loading: true, error: null });
        try {
            const result = await dispatch(fetchAdPreview({
                adId: String(row.adId),
                format,
                accountId: String(accId),
                toastOnError: false
            })).unwrap();
            drawers.update("preview", {
                loading: false,
                html: result?.html,
                url: result?.previewUrl,
                error: !result?.html && !result?.previewUrl ? "Preview unavailable" : null
            });
        }
        catch (e) {
            drawers.update("preview", {
                loading: false,
                html: null,
                url: null,
                error: e?.message || "Preview error"
            });
        }
    }, [accountOptions, connectionId, dispatch, drawers]);
    // ✅ Bulk Send Handler
    const handleBulkSend = useCallback(async () => {
        if (!selectedClientId || !selected.size)
            return;
        try {
            setBulkSending(true);
            const now = new Date();
            let mailType = "";
            if (activeTab === "social_ad_request_queue") {
                mailType = "AD_REQUEST_SEND";
            }
            else if (activeTab === "sent_for_client_approval") {
                mailType = "META_AD_REMINDER";
            }
            else {
                mailType = "META_AD_REVIEW";
            }
            await dispatch(sendEmailNotification({
                type: mailType,
                data: {
                    clientId: selectedClientId,
                    storeId: selectedClientId,
                    actionBy: entityType,
                    storeName: selectedClient?.name || "Store",
                    month: String(now.getMonth() + 1).padStart(2, "0"),
                    year: String(now.getFullYear()),
                    ads: displayRows
                        .filter(r => selected.has(r._id))
                        .map(r => ({ ad_name: r.ad_name, status: r.status, sent_count: r.sent_count })),
                    adApprovalIds: Array.from(selected),
                },
            })).unwrap();
            toast.success("Email notification sent successfully");
            await fetchData();
            setSelected(new Set());
        }
        catch (e) {
            toast.error(e?.message || "Failed to send email");
        }
        finally {
            setBulkSending(false);
        }
    }, [selectedClientId, selectedClient, selected, displayRows, entityType, dispatch, fetchData, activeTab]);
    // ✅ Client Bulk Action Handler
    const handleClientBulkAction = useCallback(async (status) => {
        if (!selectedClientId || !selected.size)
            return;
        try {
            setBulkSending(true);
            await dispatch(createApprovalActivity({
                send_by: "client",
                user_id: user?.id || user?._id || "",
                status,
                comment: "",
                campaign_ids: Array.from(selected),
                client_id: selectedClientId,
                connection_id: connectionId || "",
                module: "ad_approvals",
                hideToast: true,
            })).unwrap();
            const mailType = activeTab === "social_ad_request_queue"
                ? "AD_REQUEST_RESULT"
                : "META_AD_ACTIVITY";
            const selectedRows = displayRows.filter(r => selected.has(r._id));
            await dispatch(sendEmailNotification({
                type: mailType,
                data: {
                    clientId: selectedClientId,
                    storeId: selectedClientId,
                    actionBy: "client",
                    storeName: selectedClient?.name || "Store",
                    month: String(new Date().getMonth() + 1).padStart(2, "0"),
                    year: String(new Date().getFullYear()),
                    approvalStatus: status,
                    showHistory: false,
                    ads: selectedRows.map(r => ({ ad_name: r.ad_name, status })),
                    adApprovalIds: Array.from(selected)
                }
            })).unwrap();
            toast.success(`Action applied to ${selected.size} ads successfully`);
            await fetchData();
            setSelected(new Set());
        }
        catch (e) {
            toast.error(e?.message || `Error updating status`);
        }
        finally {
            setBulkSending(false);
        }
    }, [selectedClientId, selectedClient, selected, displayRows, user, connectionId, dispatch, fetchData, activeTab]);
    // ✅ Delete Handler
    const handleDelete = useCallback(async () => {
        if (!deleteModal.id)
            return;
        try {
            await dispatch(deleteManualAdRequest({ id: deleteModal.id })).unwrap();
            fetchData();
            setDeleteModal({ open: false, id: null });
            toast.success("Ad request deleted successfully");
        }
        catch (e) {
            toast.error(e?.message || "Delete error");
        }
    }, [dispatch, fetchData, deleteModal.id]);
    // ✅ Table Columns
    const columns = useMemo(() => isQueue
        ? [
            { id: "ad_name", label: "Ad Request Name", sortable: true, minWidth: 260 },
            { id: "test_goal", label: "Test Goal", sortable: false, minWidth: 140 },
            { id: "status", label: "Sent Status", sortable: false, minWidth: 160 },
            { id: "campaign_status", label: "Status", sortable: false, minWidth: 140 },
            { id: "actions", label: "Action", sortable: false, minWidth: 120 },
        ]
        : [
            { id: "ad_name", label: "Ad Name", sortable: true, width: 400, cellSx: () => AD_NAME_CELL_SX },
            { id: "preview", label: "Preview", sortable: false, width: 70 },
            { id: "status", label: "Sent Status", sortable: true, width: 110 },
        ], [isQueue]);
    // ✅ Render Cell
    const renderCell = useCallback((row, col) => {
        const { id } = col;
        if (id === "type" || id === "file_format") {
            return _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "14px !important", textTransform: 'capitalize' }, children: row[id] || "—" });
        }
        if (id === "ad_name") {
            const name = row.ad_name || "—";
            return (_jsx(HoverTooltip, { title: name, children: _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: "14px !important", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: name }) }));
        }
        if (id === "status") {
            let chip = getChipConfig(row.status, activeTab, row.sent_count || 0);
            return (_jsx(Box, { sx: { ...CHIP_BASE_SX, backgroundColor: chip.bg, color: chip.color, cursor: "pointer" }, onClick: () => drawers.open("action", { row }), children: chip.label }));
        }
        if (id === "campaign_status") {
            const isActionTaken = ["approved", "rejected", "not_approved"].includes(row.status?.toLowerCase() || "");
            if (isQueue && isActionTaken) {
                const isRejected = row.status === "not_approved" || row.status === "rejected";
                const label = isRejected ? "Not Approved" : "Approved";
                const color = isRejected ? "#B42318" : "#027A48";
                const bg = isRejected ? "#FEE4E2" : "#D1FADF";
                return (_jsx(Box, { sx: { ...CHIP_BASE_SX, backgroundColor: bg, color: color, fontWeight: 500, minWidth: 100 }, children: label }));
            }
            return (_jsx(Box, { sx: { ...CHIP_BASE_SX, backgroundColor: "#F5F5F5", color: "#414651", fontWeight: 500, minWidth: 100 }, children: "N/A" }));
        }
        if (id === "actions" && canManage) {
            return (_jsxs(Box, { sx: { display: "flex", gap: 1.5 }, children: [_jsx(IconButton, { size: "small", onClick: () => drawers.open("create", { editing: row }), children: _jsx("img", { src: "/assets/edit.svg", alt: "edit" }) }), _jsx(IconButton, { size: "small", onClick: () => setDeleteModal({ open: true, id: row._id }), children: _jsx("img", { src: "/assets/delete.svg", alt: "delete" }) })] }));
        }
        if (id === "preview") {
            return (_jsx(Box, { component: "button", type: "button", sx: { border: "none", backgroundColor: "transparent", cursor: "pointer", p: 0 }, onClick: () => {
                    const fmt = previewOptions[0]?.value || "";
                    drawers.open("preview", { row, format: fmt });
                    fmt ? fetchPreview(row, fmt) : toast.error("Preview unavailable");
                }, children: _jsx("img", { src: "/assets/eye.svg", alt: "preview" }) }));
        }
        return row[id] || "—";
    }, [activeTab, canManage, drawers, fetchPreview, previewOptions, isQueue]);
    // ✅ Drag Handle Component
    const DragHandle = useCallback(({ row }) => {
        if (isQueue || entityType === "client")
            return null;
        const isDragging = drag.draggingRef.current === row._id;
        return (_jsx(Box, { sx: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: canManage ? (isDragging ? "grabbing" : "grab") : "not-allowed",
                opacity: isDragging ? 1 : 0.85,
                userSelect: "none"
            }, draggable: true, onMouseDown: () => canManage && drag.onStart(row._id), onDragStart: () => canManage && drag.onStart(row._id), onDragEnd: drag.onEnd, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M10.667 4a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333m0 4.667a.667.667 0 1 0 0-1.334.667.667 0 0 0 0 1.334m0 4.666a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333M5.333 4a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333m0 4.667a.667.667 0 1 0 0-1.334.667.667 0 0 0 0 1.334m0 4.666a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333", stroke: "#A1A1AA", strokeWidth: "1.333", strokeLinecap: "round", strokeLinejoin: "round" }) }) }));
    }, [isQueue, canManage, drag, entityType]);
    // ✅ Access Check
    const dispatch_ = useDispatch();
    useEffect(() => {
        // permissions is null until /api/auth/me resolves after a fresh load —
        // treat that as "still loading", not "denied". Otherwise this effect denies
        // access, its cleanup (on the resulting unmount) clears the denial, the page
        // remounts, and the still-loading permissions deny it again — an infinite
        // mount/unmount loop that also re-fires every fetch in the page.
        if (!permissions) {
            return;
        }
        if (!permissions?.ad_approvals?.access) {
            import("../../redux/userDataSlice").then(({ setPermissionDenied }) => {
                dispatch_(setPermissionDenied({ message: "You do not have access to the Ad Approvals module. Please contact your administrator." }));
            });
            return () => {
                import("../../redux/userDataSlice").then(({ clearPermissionDenied }) => {
                    dispatch_(clearPermissionDenied());
                });
            };
        }
    }, [permissions, dispatch_]);
    if (permissions && !permissions?.ad_approvals?.access) {
        return null; // Global layout will take care of Access Denied message
    }
    const totalPages = pagination?.totalPages ?? 1;
    const total = pagination?.total ?? 0;
    const selectedAdNames = useMemo(() => displayRows.filter(r => selected.has(r._id)).map(r => r.ad_name || "Untitled Ad"), [displayRows, selected]);
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { className: "client-ad-approval-module user-list-container", sx: { pb: 10 }, children: [_jsx(ClientGroupHeader, { selectedClientId: selectedClientId, clientOptions: clientOptions, handleClientChange: handleClientChange, selectedGroupAccountId: selectedGroupAccountId, groupAccountOptions: groupAccountOptions, handleGroupChange: handleGroupChange, currentSelectedGroupAccountLabel: currentSelectedGroupAccountLabel, showAccountsField: showAccountField, selectedAccountId: connectionId, accountOptions: accountOptions, currentSelectedAccountLabel: currentAccountLabel, onAccountChange: setConnectionId, isRefreshing: isLoading || syncing || submitting, extraFilters: activeTab === "ad_approval_status" ? (_jsx(CustomDatePicker, { value: dateRange, onApply: handleDateChange, comparison: false, allowToday: true })) : null }), _jsxs(Box, { sx: { mt: 1 }, children: [_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastSyncedAt, onRefresh: handleSync, disabled: syncing, moduleName: "ad_approvals" }), rightSection: _jsx(Box, { sx: { width: 260 }, children: _jsx(SearchInput, { value: search, onSearch: setSearch, placeholder: "Search" }) }) }), _jsxs(Box, { sx: {
                                    mt: 1,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    backgroundColor: "white",
                                    p: "10px",
                                    borderTopLeftRadius: 8,
                                    borderTopRightRadius: 8,
                                    border: "1px solid #E5E7EB",
                                    borderBottom: "none"
                                }, children: [_jsx(CommonTabs, { tabs: TAB_CONFIG.map(t => ({ label: t.label, value: t.id })), selectedValue: activeTab, onChange: v => setActiveTab(v) }), isQueue && canManageAdRequests && (_jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", onClick: () => drawers.open("create", { editing: null }), children: "+ Create New Ad Request" }))] }), _jsx(Box, { className: "user-list-table-wrapper", children: _jsx(CommonTable, { columns: columns, rows: displayRows.map(r => ({ ...r, id: r._id })), renderCell: renderCell, sortConfig: sort, onSortChange: (col, dir) => setSort(dir ? { columnId: col, direction: dir } : null), expandTrigger: "none", type: "userlist-table", isLoading: rowsLoading || syncing || bulkSending, isFirstLoad: true, enableSelection: canInteract && activeTab !== "ad_approval_status", selectedRows: selected, onSelectionChange: setSelected, rowProps: (r) => (isQueue || entityType === "client") ? {} : {
                                        onDragOver: (e) => {
                                            e.preventDefault();
                                            drag.onOver(r._id, e);
                                        },
                                        onDrop: () => drag.onDrop(r._id)
                                    }, rowSx: (r) => {
                                        if (entityType === "client")
                                            return {};
                                        const isDragging = drag.draggingRef.current === r._id;
                                        const isOver = drag.dragOverRef.current === r._id;
                                        return {
                                            ...(isDragging && {
                                                opacity: 0.5,
                                                backgroundColor: "#FFF7FB"
                                            }),
                                            ...(isOver && {
                                                backgroundColor: "#FFFAEB",
                                                borderTop: "2px solid #FF008E"
                                            }),
                                        };
                                    }, beforeSelectionColumn: entityType === "client"
                                        ? undefined
                                        : {
                                            width: "32px",
                                            align: "center",
                                            header: null,
                                            renderCell: (r) => _jsx(DragHandle, { row: r })
                                        } }) }), !rowsLoading && total > 0 && (_jsx(Box, { sx: { px: "8px", mt: 1 }, children: _jsx(Pagination, { currentPage: page, totalPages: totalPages, onPageChange: setPage, maxVisiblePages: 5, totalEntries: total, itemsPerPage: PAGE_SIZE, showEntriesMessage: true }) }))] }), _jsx(AdPreview, { open: drawers.state.preview.open, previewRow: drawers.state.preview.row, previewFormat: drawers.state.preview.format, previewFormatOptions: previewOptions, loading: drawers.state.preview.loading, error: drawers.state.preview.error, html: drawers.state.preview.html, previewUrl: drawers.state.preview.url, onClose: () => drawers.close("preview", { row: null, html: null, url: null, error: null }), onPreviewFormatChange: fmt => {
                            drawers.update("preview", { format: fmt });
                            fetchPreview(drawers.state.preview.row, fmt);
                        } }), _jsx(CreateAdRequestDrawer, { open: drawers.state.create.open, connectionId: connectionId, editingAdRequest: drawers.state.create.editing, onClose: () => drawers.close("create", { editing: null }), onSuccess: fetchData, onSubmittingChange: setSubmitting }), _jsx(CommonDrawerLayout, { open: drawers.state.action.open && !!drawers.state.action.row, onClose: () => drawers.close("action", { row: null }), title: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: ["Take Action", drawers.state.action.row?.ad_name && (_jsxs(_Fragment, { children: [_jsx(Typography, { sx: { color: "#9CA3AF", fontWeight: 400 }, children: "|" }), _jsx(HoverTooltip, { title: drawers.state.action.row.ad_name, children: _jsx(Typography, { sx: {
                                                    maxWidth: 300,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    fontWeight: 600,
                                                    color: "#111827",
                                                    fontSize: "16px"
                                                }, children: drawers.state.action.row.ad_name }) })] }))] }), width: "md", children: drawers.state.action.row && (_jsx(Box, { sx: { p: 1 }, children: _jsx(TakeActionContent, { client_id: drawers.state.action.row.client_id || selectedClientId, connection_id: drawers.state.action.row.connection_id || connectionId, campaign_id: drawers.state.action.row._id, currentStatus: drawers.state.action.row.status, marketingcalenderRefresh: false, is_upload_show: false, clientAdApprovals: true, isQueue: isQueue, data: {
                                    ...drawers.state.action.row,
                                    storeName: selectedClient?.name || "Store"
                                }, hideUpdateForm: !canInteract }) })) }), _jsx(ConfirmActionModal, { open: deleteModal.open, title: "Delete Ad Request", description: "Are you sure you want to delete this ad request? This action cannot be undone.", onConfirm: handleDelete, onClose: () => setDeleteModal({ open: false, id: null }), confirmText: "Delete", isDeleteIcon: true })] }), _jsx(FloatingSaveBar, { count: selected.size, onReset: () => setSelected(new Set()), isVisible: canInteract && selected.size > 0, message: "Ads selected for bulk action", countLabel: "Ads Selected", details: selectedAdNames, children: isClient ? (_jsxs(Box, { sx: { display: 'flex', gap: 1.5 }, children: [_jsx(Button, { onClick: () => handleClientBulkAction("rejected"), disabled: bulkSending, sx: {
                                textTransform: "none", bgcolor: '#D92D20', color: '#fff', fontWeight: 700, px: 3, height: "42px",
                                borderRadius: "8px", boxShadow: "0 4px 12px rgba(217, 45, 32, 0.25)",
                                "&:hover": { bgcolor: '#B42318' },
                                "&.Mui-disabled": { bgcolor: "#fca5a5", color: "#fff" }
                            }, children: bulkSending ? _jsx(CircularProgress, { size: 16, color: "inherit" }) : "Not Approved" }), _jsx(Button, { onClick: () => handleClientBulkAction("approved"), disabled: bulkSending, sx: {
                                textTransform: "none", bgcolor: '#027A48', color: '#fff', fontWeight: 700, px: 3, height: "42px",
                                borderRadius: "8px", boxShadow: "0 4px 12px rgba(2, 122, 72, 0.25)",
                                "&:hover": { bgcolor: '#026038' },
                                "&.Mui-disabled": { bgcolor: "#86efac", color: "#fff" }
                            }, children: bulkSending ? _jsx(CircularProgress, { size: 16, color: "inherit" }) : "Approve" })] })) : (_jsx(Button, { onClick: handleBulkSend, disabled: bulkSending, sx: {
                        textTransform: "none", bgcolor: "#ff008e", color: "#fff", fontWeight: 700, px: 3, height: "42px",
                        borderRadius: "8px", boxShadow: "0 4px 12px rgba(255, 0, 142, 0.25)",
                        "&:hover": { bgcolor: "#e60080" },
                        "&.Mui-disabled": { bgcolor: "#fca5d2", color: "#fff" }
                    }, children: bulkSending ? _jsx(CircularProgress, { size: 16, color: "inherit" }) :
                        (activeTab === "sent_for_client_approval" ? "Re-Send for Approval" : "Send for Approval") })) })] }));
};
export default ClientAdApprovalPage;
